/**
 * Single source of truth for BackOffice sidebar (and top-menu) navigation.
 * Paths and labels extracted from live SideBarComponents — do not rename without product sign-off.
 */

/**
 * @param {string} pathname
 * @param {string|string[]|undefined} matchOrPath
 * @param {{ end?: boolean }} [options]
 */
export function isPathActive(pathname, matchOrPath, options = {}) {
  if (!matchOrPath) return false;
  const path = (pathname || "").toLowerCase();
  const { end = false } = options;
  const matches = Array.isArray(matchOrPath) ? matchOrPath : [matchOrPath];

  return matches.filter(Boolean).some((raw) => {
    const target = String(raw).toLowerCase();
    if (end) {
      return path === target || path === `${target}/`;
    }
    if (target === "/" || target === "/dashboard") {
      return path === "/" || path === "/dashboard" || path.startsWith("/dashboard/");
    }
    return path === target || path.startsWith(`${target}/`) || path.startsWith(target);
  });
}

/** Find the top-level menu id whose match hits the current path (for accordion auto-expand). */
export function findActiveSectionId(pathname, menus = Menu) {
  for (const item of menus) {
    if (!item.children?.length) continue;
    if (isPathActive(pathname, item.match || item.path)) {
      return item.id;
    }
  }
  return null;
}

const Menu = [
  {
    id: "dashboard",
    path: "/dashboard",
    icon: "fa fa-sitemap",
    title: "Dashboard",
    match: ["/", "/dashboard"],
  },
  {
    id: "products",
    path: "/items/manageItems",
    icon: "fa-solid fa-utensils",
    title: "Products",
    match: [
      "/items",
      "/categories",
      "/modifiers",
      "/menu",
      "/manageReportingCategory",
      "/manageCategoryDepartments",
      "/discount",
      "/voucher",
      "/supplier",
      "/deal",
    ],
    children: [
      {
        path: "/manageCategoryDepartments",
        title: "Department",
        match: ["/manageCategoryDepartments"],
      },
      {
        path: "/manageReportingCategory",
        title: "Reporting Categories",
        match: ["/manageReportingCategory"],
      },
      {
        path: "/categories/manageCategories",
        title: "Categories",
        match: ["/categories"],
      },
      {
        path: "/modifiers/manageModifierGroups",
        title: "Modifier Groups",
        match: ["/modifiers/manageModifierGroups", "/modifiers/addModifierGroup", "/modifiers/updateModifierGroup"],
      },
      {
        path: "/modifiers/manageModifiers",
        title: "Modifiers",
        match: ["/modifiers/manageModifiers"],
      },
      {
        path: "/items/manageItems",
        title: "Items",
        match: ["/items"],
      },
      {
        path: "/deal/manageDeals",
        title: "Combo Deals",
        match: ["/deal"],
      },
      {
        path: "/menu/manageMenu",
        title: "Menus",
        match: ["/menu"],
      },
      {
        path: "/supplier/manageSupplier",
        title: "Suppliers",
        match: ["/supplier"],
      },
      {
        path: "/discount/manageDiscount",
        title: "Discounts",
        match: ["/discount"],
      },
      {
        path: "/voucher/manageVoucher",
        title: "Voucher",
        match: ["/voucher"],
      },
    ],
  },
  {
    id: "customers",
    path: "/customer/manageCustomer",
    icon: "fa-solid fa-users-line",
    title: "Customers",
    match: ["/customer", "/customerGroup", "/loyalty"],
    children: [
      {
        path: "/customer/manageCustomer",
        title: "Customers",
        match: ["/customer/"],
      },
      {
        path: "/customerGroup/manageCustomerGroup",
        title: "Customer Groups",
        match: ["/customerGroup/"],
      },
      {
        path: "/loyalty/rules",
        title: "Loyalty Rule",
        match: ["/loyalty/rules"],
      },
      {
        path: "/loyalty/rewards",
        title: "Loyalty Rewards",
        match: ["/loyalty/rewards", "/loyalty/updateReward", "/loyalty/createReward"],
      },
    ],
  },
  {
    id: "marketing",
    path: "/marketing/manageMarketingGroups",
    icon: "fa-solid fa-bullhorn",
    title: "Marketing",
    match: ["/marketing", "/campaigns"],
    children: [
      {
        path: "/marketing/manageMarketingGroups",
        title: "Marketing Groups",
        match: [
          "/marketing/managemarketinggroups",
          "/marketing/addmarketinggroup",
          "/marketing/updatemarketinggroup",
        ],
      },
      {
        path: "/marketing/manageSmsPresets",
        title: "SMS Preset",
        match: ["/marketing/managesmspresets"],
      },
      {
        path: "/marketing/manageEmailPresets",
        title: "Email Preset",
        match: [
          "/marketing/manageemailpresets",
          "/marketing/addemailpreset",
          "/marketing/updateemailpreset",
        ],
      },
      {
        path: "/marketing/marketingSettings",
        title: "Marketing Settings",
        match: ["/marketing/marketingsettings"],
      },
      {
        path: "/campaigns/manageCampaigns",
        title: "Campaigns",
        match: ["/campaigns"],
      },
    ],
  },
  {
    id: "staffs",
    path: "/staff/manageStaff",
    icon: "fas fa-users",
    title: "Staffs",
    match: ["/staff", "/role"],
    children: [
      {
        path: "/staff/manageStaff",
        title: "Staffs",
        match: [
          "/staff/managestaff",
          "/staff/addstaff",
          "/staff/updatestaff",
          "/staff/updatepassword",
          "/staff/managescheduleroster",
          "/staff/staffshiftreport",
          "/staff/payout",
        ],
      },
      {
        path: "/role/manageRole",
        title: "Role",
        match: ["/role"],
      },
    ],
  },
  {
    id: "orders",
    path: "/Order/manageOrder",
    icon: "fa-solid fa-utensils",
    title: "Orders",
    match: ["/Order"],
    children: [
      {
        path: "/Order/manageOrder",
        title: "Orders",
        match: ["/Order"],
      },
    ],
  },
  {
    id: "dining-in",
    path: "/tableManagement/serviceTime",
    icon: "fa-solid fa-table",
    title: "Dining In",
    match: [
      "/tableManagement/serviceTime",
      "/diningAreas",
      "/serviceArea",
    ],
    children: [
      {
        path: "/tableManagement/serviceTime",
        title: "Service Time",
        match: ["/tableManagement/serviceTime"],
      },
      {
        path: "/diningAreas",
        title: "Dining Areas",
        match: ["/diningAreas"],
      },
    ],
  },
  {
    id: "reservations",
    path: "/tableManagement/reservation",
    icon: "fa-solid fa-table",
    title: "Reservations",
    match: [
      "/tableManagement/reservation",
      "/tableManagement/specialItems",
      "/tableManagement/menuPresets",
      "/tableManagement/manageAdvanceDeposit",
      "/tableManagement/addAdvanceDeposit",
      "/tableManagement/updateAdvanceDeposit",
    ],
    children: [
      {
        path: "/tableManagement/reservation",
        title: "Reservation List",
        match: ["/tableManagement/reservation"],
      },
      {
        path: "/tableManagement/specialItems",
        title: "Special Items",
        match: ["/tableManagement/specialItems"],
      },
    ],
  },
  {
    id: "report",
    path: "/report/customerReport",
    icon: "fa-solid fa-user-tie",
    title: "Report ",
    match: ["/report"],
    children: [
      {
        path: "/report/customerReport",
        title: "Customer Report",
        match: ["/report/customerReport"],
      },
      {
        path: "/report/AllStaffShiftReport",
        title: "Staff Shift Report",
        match: ["/report/AllStaffShiftReport"],
      },
      {
        path: "/report/staffWorkReport",
        title: "Staff Work Report",
        match: ["/report/staffWorkReport"],
      },
      {
        path: "/report/salesBreakdownByTime",
        title: "Sales Breakdown by Time",
        match: ["/report/salesBreakdownByTime"],
      },
      {
        path: "/report/salesByProducts",
        title: "Sales By Products",
        match: ["/report/salesByProducts"],
      },
      {
        path: "/report/dealPerformance",
        title: "Combo Deal Performance",
        match: ["/report/dealPerformance"],
      },
      {
        path: "/report/tableSpent",
        title: "Table Spent",
        match: ["/report/tableSpent"],
      },
      {
        path: "/report/tableCover",
        title: "Table Cover Report",
        match: ["/report/tableCover"],
      },
      {
        path: "/report/zReport",
        title: "Z-Report",
        match: ["/report/zReport"],
      },
      {
        path: "/report/cashReport",
        title: "Petty Cash Report",
        match: ["/report/cashReport"],
      },
      {
        path: "/report/loyaltyReport",
        title: "Loyalty Report",
        match: ["/report/loyaltyReport"],
      },
    ],
  },
  {
    id: "catering",
    path: "/catering/services/manageServices",
    icon: "fa-solid fa-utensils",
    title: "Catering",
    match: ["/catering"],
    children: [
      {
        path: "/catering/services/manageServices",
        title: "Services",
        match: ["/catering/services"],
      },
    ],
  },
  {
    id: "administration",
    path: "/profile",
    icon: "fa-solid fa-user-tie",
    title: "Administration",
    match: [
      "/profile",
      "/setting",
      "/manageDevices",
      "/paymentMethod",
      "/messaging/twilioSettings",
      "/messaging/emailSenderSettings",
      "/messaging/smsTopUps",
      "/pettyCash",
      "/subscription-invoices",
    ],
    children: [
      {
        path: "/profile",
        title: "Account Info",
        match: ["/profile"],
        end: true,
      },
      {
        path: "/setting",
        title: "General Setting",
        match: ["/setting"],
        end: true,
      },
      {
        path: "/subscription-invoices",
        title: "Subscription Invoices",
        match: ["/subscription-invoices"],
        end: true,
      },
      {
        path: "/setting/promotion-banners",
        title: "CDS Banners",
        match: ["/setting/promotion-banners"],
        end: true,
      },
      {
        path: "/setting/online-ordering-banners",
        title: "Online Ordering Banners",
        match: ["/setting/online-ordering-banners"],
        end: true,
      },
      {
        path: "/manageDevices",
        title: "POS Terminals",
        match: ["/manageDevices"],
        end: true,
      },
      {
        path: "/paymentMethod",
        title: "Payment Gateways",
        match: ["/paymentMethod"],
        end: true,
      },
      {
        path: "/messaging/twilioSettings",
        title: "Twilio Settings",
        match: ["/messaging/twilioSettings"],
        end: true,
      },
      {
        path: "/messaging/smsTopUps",
        title: "SMS Top-ups",
        match: ["/messaging/smsTopUps"],
        end: true,
      },
      {
        path: "/messaging/emailSenderSettings",
        title: "Email Sender",
        match: ["/messaging/emailSenderSettings"],
        end: true,
      },
      // {
      //   path: "/setting/Pricing&Subscriptions",
      //   title: "Subscription Plans",
      //   match: ["/setting/Pricing&Subscriptions"],
      //   end: true,
      // },
      {
        path: "/pettyCash/managePettyCash",
        title: "Expense Heads",
        match: ["/pettyCash/managePettyCash"],
        end: true,
      },
    ],
  },
];

export default Menu;
